// Card #1652 (design §6.1/§6.2 type 5) — the most prominent element of the
// Post-Ride / Score single-entry layout: the combined confirmed result.
// Copy is fixed to "confirmed score" (design §4.5/§6.2: never "final
// score"). `scoreHero` is a percentage number, already status-gated
// server-side (score-api's lib/scoreboard-mode-payload.js composePostRideScore
// only emits it once the ride is CONFIRMED/FINAL, never while PARTIAL) —
// this component only has to decide whether to render at all (undefined ->
// nothing) and how to format the number.
//
// Card #1694 AC4 — formatting goes through score-common's formatPercentage
// (3 decimals, single shared formatter across the FEs) rather than a local
// `.toFixed()`; it already Number()-coerces and falls back to "—" for a
// non-numeric value, so this kiosk still never white-screens on a bad wire
// value (no error boundary here).
import { formatPercentage } from "score-common";
import { isScorePubliclyVisible } from "./publicScoreVisibility";
import "./ScoreHero.css";

// Card #2312 — a terminated ride (Scratched/Excused/Eliminated/No Show,
// score-api's scoreboard-mode-payload.js composePostRideScore, already
// validated against score-common's TERM_REASONS/isKnownTermReason before it
// ever reaches this prop) shows its outcome here INSTEAD of the percentage,
// never both, never a bare 0/suppressed number in its place — the same
// contract RankedList's Score column (#2060) and ClassResultsPage's result
// column (#2104) already apply. `termReason` wins over `scoreHero` because a
// terminated ride's composite is always 0 or null: buildRideResult zeroes it
// directly for No Show and Excused (results.js:733,:792); an ordinary
// sheet-level elimination falls through to the normal return and gets its 0
// from score-common's calculation.js:459 instead. Either way, the two are
// never both a real number to choose between. It carries its own
// `.score-hero__term-reason` class rather than reusing `.score-hero__value`:
// that class is sized for a seven-glyph number and clipped the ten-glyph
// "Eliminated" off the board's edge (ScoreHero.css).
export default function ScoreHero({ scoreHero, termReason }) {
  if (termReason) {
    return (
      <div className="score-hero" data-testid="score-hero">
        <p className="score-hero__term-reason" data-testid="score-hero-term-reason">
          {termReason}
        </p>
      </div>
    );
  }

  if (scoreHero == null) return null;

  // Card #2727 -- ScoreHero's OWN floor, not a repeat of a caller's. Before
  // this card, only SingleEntryLayout's shouldShowPostRideResult gated the
  // sub-55 case (dropping the whole `.single-entry__result` column) --
  // ScoreHero itself printed whatever number it was handed. Any other
  // parent, any new view type, or a bug in that one caller's condition would
  // have reached this line with a real sub-55 percentage and rendered it,
  // which is exactly the "one caller's if statement" this card's title
  // names. Checking isScorePubliclyVisible here too means ScoreHero refuses
  // on its own, with no help from any parent (AC1) -- same not-visible
  // outcome as `scoreHero == null` above (no caption, no number), never a
  // bare/broken hero with a caption over nothing.
  if (!isScorePubliclyVisible(Number(scoreHero))) return null;

  return (
    <div className="score-hero" data-testid="score-hero">
      <p className="score-hero__caption" data-testid="score-hero-caption">
        confirmed score
      </p>
      <p className="score-hero__value" data-testid="score-hero-value">
        {formatPercentage(Number(scoreHero))}
      </p>
    </div>
  );
}
